import React, { useState, useCallback, useEffect } from 'react';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import WizardContext from '../WizardContext';

export default function Wizard({
  steps = [],
  onClose,
  onComplete,
  onDataChange,
  onStepChange,
  currentStepIndex: controlledStepIndex,
  initialData = {},
  title
}) {
  const [internalStepIndex, setInternalStepIndex] = useState(0);
  const [data, setData] = useState(initialData);

  // Sync internal data with initialData when it changes (critical for prop-driven updates like IDs)
  useEffect(() => {
     if (initialData && Object.keys(initialData).length > 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setData(prevData => {
           // efficient deep comparison or just compare keys relevant to the wizard
           if (JSON.stringify(initialData) === JSON.stringify(prevData)) return prevData;
           return { ...prevData, ...initialData };
        });
     }
  }, [initialData]);

  const isControlled = typeof controlledStepIndex !== 'undefined';
  const currentStepIndex = isControlled ? controlledStepIndex : internalStepIndex;

  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  const currentStep = steps[currentStepIndex];

  const nextStep = useCallback((stepData = {}) => {
    const newData = { ...data, ...stepData };
    setData(newData);
    if (onDataChange) {
      onDataChange(newData);
    }

    if (isControlled) {
      if (onStepChange) {
        onStepChange(currentStepIndex + 1, newData);
      }
    } else {
      if (isLastStep) {
        if (onComplete) {
          onComplete(newData);
        }
      } else {
        setInternalStepIndex((prev) => prev + 1);
      }
    }
  }, [data, isLastStep, onComplete, onDataChange, isControlled, onStepChange, currentStepIndex]);

  const prevStep = useCallback(() => {
    if (!isFirstStep) {
      if (isControlled && onStepChange) {
        onStepChange(currentStepIndex - 1, data);
      } else {
        setInternalStepIndex((prev) => prev - 1);
      }
    }
  }, [isFirstStep, isControlled, onStepChange, currentStepIndex, data]);

  const goToStep = useCallback((index) => {
    if (index >= 0 && index < steps.length) {
      if (isControlled && onStepChange) {
        onStepChange(index, data);
      } else {
        setInternalStepIndex(index);
      }
    }
  }, [steps.length, isControlled, onStepChange, data]);

  const contextValue = {
    currentStepIndex,
    totalSteps: steps.length,
    isFirstStep,
    isLastStep,
    data,
    nextStep,
    prevStep,
    goToStep,
    onClose
  };

  const CurrentComponent = currentStep?.component;

  return (
    <WizardContext.Provider value={contextValue}>
      <div className="min-h-screen bg-gray-900 flex flex-col">
        <header className="bg-gray-800 border-b border-white/10 sticky top-0">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center w-10">
                {!isFirstStep ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="rounded-full p-1 text-gray-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    <span className="sr-only">Back</span>
                    <ArrowLeftIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                ) : null}
              </div>
              <div className="flex flex-col items-center flex-1">
                {title && <h1 className="text-sm font-medium text-gray-400 mb-2">{title}</h1>}
                <div className="flex items-center space-x-1.5">
                  {steps.map((_, index) => (
                    <div
                      key={index}
                      className={`h-1.5 w-8 rounded-full transition-colors duration-300 ${
                        index <= currentStepIndex ? 'bg-indigo-500' : 'bg-gray-700'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center w-10 justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full p-1 text-gray-400 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
            {CurrentComponent ? (
              <div className="animate-fade-in">
                <CurrentComponent />
              </div>
            ) : (
              <div className="text-white text-center">Step content not found</div>
            )}
          </div>
        </main>
      </div>
    </WizardContext.Provider>
  );
}
